import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from "react";

import { DialogsProvider } from "@/components/shell/Dialogs";
import { MenuProvider } from "@/components/shell/Menu";
import { Rail } from "@/components/shell/Rail";
import { TabBar } from "@/components/shell/TabBar";
import { MoreSheet } from "@/components/shell/MoreSheet";
import { TopBar } from "@/components/shell/TopBar";
import { causedHere } from "@/lib/clientId";
import { notifyCollectionChanged } from "@/lib/collectionEvents";
import { API_BASE } from "@/lib/config";
import { destination, type DestinationId } from "@/lib/destinations";
import { listJobs, subscribeToJobs } from "@/lib/jobs";
import { bindOverlayApi } from "@/lib/navBridge";
import { EMPTY_CARDS_URL_STATE, type CardsUrlState } from "@/lib/navUrl";
import { useHistoryNav } from "@/lib/useHistoryNav";
import { useDeckCounts } from "@/lib/useDeckCounts";
import { usePhone } from "@/lib/usePhone";
import { useRefreshOnFocus } from "@/lib/useRefreshOnFocus";
import { NotBuiltYet } from "@/surfaces/NotBuiltYet";
import { Cards } from "@/surfaces/cards/Cards";
import { Read } from "@/surfaces/read/Read";
import { Review } from "@/surfaces/review/Review";
import { captureFromQuery } from "@/surfaces/read/captureImage";
import type { SlotKey } from "@/surfaces/read/ImageSlots";
import { wrapImageValue } from "@/surfaces/read/mediaSlots";
import { useAgentBridge } from "@/surfaces/read/useAgentBridge";
import { useReading } from "@/surfaces/read/useReading";
import { Grammar } from "@/surfaces/grammar/Grammar";
import { Groups } from "@/surfaces/groups/Groups";
import { Search } from "@/surfaces/search/Search";
import { Setup } from "@/surfaces/setup/Setup";
import { Stats } from "@/surfaces/stats/Stats";
import { SplitPane } from "@/components/shell/SplitPane";

export default function App() {
  /**
   * Where you are, and the only writer of the address bar and the history stack.
   *
   * This replaced four separate holdings of navigation state — the active tab and Cards' filters
   * here, the library and its move-intent in `Read`, the two drill-ins in `Setup` — plus the two
   * `replaceState` sites that had to be declared in the right order to avoid resurrecting the
   * capture link. See `navUrl.ts` for the shape and `navHistory.ts` for what earns a history entry.
   */
  const { nav, api, epoch, initialSearch } = useHistoryNav();

  const isPhone = usePhone();
  const [synced, setSynced] = useState(true);
  /**
   * The Setup badge: jobs whose last run failed and that asked to be told.
   *
   * Held here rather than in Setup because that is the point — a job that failed at 03:00 has to
   * be visible from wherever you happen to be, not only once you go looking.
   */
  const failedJobs = useFailedJobCount();

  /**
   * The reading library — folders, logs, the open tabs, and the log you are reading.
   *
   * Held here rather than in `Read`, which a rail click unmounts. Since 日本-207 the *records* are
   * server state, so an unmount no longer destroys anything — but the open tabs, the active tab and
   * the loaded sentences still live in this hook, and a capture still has to be able to arrive
   * while you are on another surface. See `useReading`.
   */
  const log = useReading();

  /**
   * Screenshots the tray agent is still uploading, keyed by the id it minted.
   *
   * A ref, not state: nothing renders from it. The slot's pending mark lives on the log entry, and
   * this is only the lookup that turns the server's `mediaStored` frame back into "that sentence,
   * that slot". Entries are removed as they resolve; one that never does costs a map entry for the
   * life of the tab, which is the right trade against a timer that could expire a slow upload.
   */
  const pendingShots = useRef(new Map<string, { sentenceId: string; slot: SlotKey }>());

  /**
   * The deck counters, read here for the whole app — the rail badge AND Read's top bar.
   *
   * They used to be two identical fetches of one URL, and the second one was not merely wasteful:
   * the endpoint runs on the interactive handle's single collection worker thread, so the duplicate
   * occupied the executor that Read's own lookups queue behind. `useDeckCounts` explains the rest,
   * including why the optimistic ±1 guard had to travel with the fetch.
   *
   * App is also the right owner on its own merits: it never unmounts, so the numbers now survive a
   * trip to Cards and back instead of flashing absent while Read remounts and re-asks.
   */
  const { counts, adjust: adjustCounts } = useDeckCounts();
  // `dueToday`, not `due`: the badge is the day's obligation, so it has to be what the scheduler
  // would actually hand you — capped by the deck's limits and its parents'. `due` is the raw pile,
  // which on a real collection is an order of magnitude larger and says nothing about today.
  //
  // Zero while the hub is unreadable, which is deliberately the OPPOSITE of what Read does with the
  // same payload: a badge of 0 beats a fabricated number, whereas a confident `0 added` after a
  // morning's mining is worse than no answer at all. One fetch, two rendering rules.
  const dueCount = counts?.dueToday ?? 0;

  /**
   * Lend the overlay half of the api to `LightboxProvider`, which mounts *above* this component.
   * See `navBridge.ts` — it is the one thing that may not take the api as a prop.
   */
  useEffect(() => {
    bindOverlayApi(api);
    return () => bindOverlayApi(null);
  }, [api]);

  // The one place the SSE stream is turned into "counts are stale". Deliberately not folded into
  // `useFailedJobCount` — that hook's name would stop being true, and this signal is nothing to do
  // with failure.
  //
  // This used to pattern-match a terminal `jobStatus`, which meant only a JOB finishing ever
  // refreshed anything: an MCP `add_note`, a Cowork bulk pass and deck or tag admin all changed
  // exactly these counts and announced nothing. Every write now publishes `collectionChanged`, so
  // the job branch is gone rather than kept alongside — keeping both would fire twice per job, and
  // the old one also fired for a job that wrote nothing at all.
  //
  // Ignoring our own writes is what lets this be unconditional everywhere else: the surface that
  // wrote has already updated itself and holds an optimistic ±1, so refetching here would fight it.
  useEffect(
    () =>
      subscribeToJobs({
        onCollectionChanged: ({ writer }) => {
          if (causedHere(writer)) return;
          notifyCollectionChanged();
        },
        // The tray agent's screenshot, arriving after the sentence it belongs to. `pendingShots`
        // holds the only thing that can say which sentence that was: the agent minted the id before
        // it started uploading, and the filename is not usable as a key because Anki may store the
        // file under a different name than it was handed.
        //
        // An unknown id is another tab's capture — the stream fans out to every subscriber — so it
        // is dropped without comment rather than treated as an error.
        //
        // `setImage` is keyed by SENTENCE id and searches every loaded log, which is what makes
        // this survive a tab switch: the upload takes seconds, and moving to another log in the
        // meantime must not silently drop the screenshot.
        onMediaStored: ({ captureId, value }) => {
          const pending = pendingShots.current.get(captureId);
          if (!pending) return;
          pendingShots.current.delete(captureId);
          log.setImage(pending.sentenceId, pending.slot, value);
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Capture arrival, path one: the tray agent's deep link, `/?q=<ocr text>`. Used only when no tab
  // was listening, so it is the first capture of a session.
  //
  // The ref guard is load-bearing, not defensive: StrictMode intentionally double-invokes mount
  // effects in development, which appended the captured sentence to the log TWICE. An empty
  // dependency array is not an "only once" guarantee.
  //
  // Read from `initialSearch` — the query string as it was on the first render — rather than from
  // the live location. `useHistoryNav` strips the capture params at boot, and reading a snapshot
  // means this no longer has to run before that write does. That ordering was load-bearing,
  // undocumented, and would have broken the first time somebody moved an effect.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    const params = new URLSearchParams(initialSearch);
    const q = params.get("q");
    // A capture region may pin a log (日本-463). On this path the agent had to put it in the URL:
    // it is opening the very tab that would otherwise have been told over the bridge.
    const targetLogId = params.get("log");
    // On this path the screenshot has already been uploaded — the agent had to name a real file to
    // put it in a URL, because it is opening the very tab that would otherwise have received the
    // `mediaStored` event — so the slot is filled outright and nothing is left pending.
    const shot = captureFromQuery(initialSearch);
    if (!q) return;
    void log.append(q, undefined, targetLogId).then((id) => {
      if (id !== null && shot) log.setImage(id, shot.slot, wrapImageValue(shot.filename));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Path two: with a tab already open the agent pushes over SSE instead of opening a second one.
  // Keyed on the nonce so two identical captures both append.
  //
  // A capture is the one event allowed to move you: the alternative is the agent deciding no tab
  // is listening and opening another one, which is the thing this whole path exists to prevent.
  // It pushes rather than replaces, so Back returns you to whatever it interrupted.
  const { push: pushed, shotFailure, action: agentAction } = useAgentBridge();
  useEffect(() => {
    if (!pushed) return;
    api.navigate({ tab: "read", read: { view: "log" } });
    // Nothing here opens the target log's tab: `append` does it, for every path at once — see
    // `place` in `useReading`. Doing it here as well would open a tab for an id this device does
    // not know, which the append itself deliberately refuses to honour.
    const shot = pushed.capture;
    // Registered in `onCreated`, which runs SYNCHRONOUSLY inside `append` — not in the `.then`.
    // The promise waits on the parse request, and the agent started uploading before it sent this
    // frame, so `mediaStored` can land first; registering late would drop it and leave the slot
    // pending forever.
    void log.append(
      pushed.text,
      (id) => {
        if (!shot) return;
        pendingShots.current.set(shot.captureId, { sentenceId: id, slot: shot.slot });
        log.beginImage(id, shot.slot);
      },
      pushed.logId,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pushed?.nonce]);

  // The agent could not upload a screenshot it already told us to expect. Nothing else can clear
  // that slot: the success signal comes from the server, which never saw these bytes.
  useEffect(() => {
    if (!shotFailure) return;
    const pending = pendingShots.current.get(shotFailure.captureId);
    if (!pending) return;
    pendingShots.current.delete(shotFailure.captureId);
    // An empty value rather than an error line. The slot goes back to being an ordinary empty one —
    // clearable, droppable, pasteable — which is the state you want to be left in, and the tray's
    // Screenshot ▸ Uploads row is where the reason lives.
    log.setImage(pending.sentenceId, pending.slot, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shotFailure?.nonce]);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/sync/status`, { cache: "no-store" });
        if (!cancelled) setSynced(response.ok && !(await response.json()).lastError);
      } catch {
        if (!cancelled) setSynced(false);
      }
    };
    // Poll, don't sample once: the dot is the app's only sync-health surface, and a hub that
    // dies an hour into a session should turn it off. The endpoint never syncs, so this is a
    // local status read, not a per-interval hub round-trip.
    void poll();
    const handle = window.setInterval(() => void poll(), 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, []);

  const navigate = useCallback(
    (id: DestinationId) => api.navigate({ tab: id }),
    [api],
  );

  /**
   * The phone's その他 sheet (日本-497).
   *
   * A **layer**, not a tab: it writes a history entry and no URL, so `nav.tab` never stops naming a
   * real destination and Back closes the sheet instead of leaving the surface. Every close routes
   * through `closeMore` so the entry is consumed exactly once — the rule `Read`'s picker already
   * follows, and the reason it has to be one setter rather than three call sites.
   */
  const [moreOpen, setMoreOpenState] = useState(false);
  const openMore = useCallback(() => {
    setMoreOpenState(true);
    api.openOverlay("more");
  }, [api]);
  const closeMore = useCallback(() => {
    setMoreOpenState(false);
    api.closeOverlay("more");
  }, [api]);
  // Closing it *because* of a Back: the entry is already gone, so this must not consume another.
  useEffect(() => api.registerOverlay("more", () => setMoreOpenState(false)), [api]);

  /**
   * Cards' filters, as the setter that surface already speaks.
   *
   * Which of these writes a history entry is `classify`'s decision, not this adapter's: moving the
   * open dock to another note is a place, and every chip, deck and keystroke beside it is a
   * refinement of where you already are.
   */
  const setCardFilters = useCallback(
    (update: SetStateAction<CardsUrlState>, opts?: { replace?: boolean }) => {
      api.navigate(
        (prev) => ({ cards: typeof update === "function" ? update(prev.cards) : update }),
        opts,
      );
    },
    [api],
  );

  /** A group page's own copy of 単語's filters (日本-601) — never `nav.cards`, see `GroupsUrlState`. */
  const setGroupFilters = useCallback(
    (update: SetStateAction<CardsUrlState>, opts?: { replace?: boolean }) => {
      api.navigate(
        (prev) => ({
          groups: {
            cards: typeof update === "function" ? update(prev.groups.cards) : update,
          },
        }),
        opts,
      );
    },
    [api],
  );

  /**
   * Read's log, with the three acts that are navigation wrapped to say so.
   *
   * Wrapped here rather than inside `useReading` because that hook owns the *data* — which logs
   * exist and what is in them — and has no business knowing about history. Opening a log from the
   * library is deliberately one composite act: it leaves the library and changes the log together,
   * so Back from it returns to the library rather than to the same log seen through it.
   */
  const readingLog = useMemo(
    () => ({
      ...log,
      setActiveLog: (logId: string) => {
        log.setActiveLog(logId);
        api.navigate({ read: { log: logId } });
      },
      openLog: (logId: string) => {
        log.openLog(logId);
        api.navigate({ read: { view: "log", log: logId, moving: null } });
      },
      newLog: async () => {
        const id = await log.newLog();
        if (id) api.navigate({ read: { view: "log", log: id, moving: null } });
        return id;
      },
      // `closeTab` is deliberately NOT wrapped: the place is gone rather than behind you, so the
      // active-log change it causes is amended onto the current entry by the effect below.
    }),
    [log, api],
  );

  /**
   * Keep `nav.read.log` and `useReading`'s active tab agreeing, in whichever direction is right.
   *
   * Three cases, and which one applies is the whole subtlety.
   *
   * **At boot the URL wins, once.** `?read.log=<id>` is what makes a log linkable — the reason it
   * is in the URL at all — and the tab state it would otherwise lose to is `localStorage`, which is
   * per device by design. Without this the address bar is rewritten to whichever log this machine
   * happened to have open and the link silently does nothing, which is indistinguishable from the
   * feature not existing. It waits for `ready`: the library read decides whether the id is real,
   * and an id checked against an empty list always looks stale.
   *
   * **After a pop the entry wins** — that is Back moving you between reading tabs.
   *
   * **Otherwise the reading state wins**, and amends the current entry rather than adding one:
   * this is what carries a tab close and a log deleted on another device.
   *
   * A log named by neither an entry nor a link that no longer exists is dropped rather than opened
   * as a tab that 404s, which is `logTabs.reconcile`'s rule applied to the same question.
   */
  const syncedEpoch = useRef(epoch);
  const bootResolved = useRef(false);
  useEffect(() => {
    const popped = syncedEpoch.current !== epoch;
    syncedEpoch.current = epoch;

    if (!bootResolved.current) {
      if (!log.ready) return;
      bootResolved.current = true;
      const linked = nav.read.log;
      if (linked && linked !== log.activeLogId && log.logs.some((e) => e.id === linked)) {
        log.openLog(linked);
        return;
      }
    }

    if (log.activeLogId === nav.read.log) return;

    if (popped) {
      const wanted = nav.read.log;
      if (wanted && log.logs.some((entry) => entry.id === wanted)) {
        log.openLog(wanted);
        return;
      }
    }
    api.navigate({ read: { log: log.activeLogId } }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [epoch, log.ready, log.activeLogId, log.logs, nav.read.log, api]);

  const active = nav.tab;

  const tabBar = (
    <TabBar
      active={active}
      onNavigate={navigate}
      dueCount={dueCount}
      failedJobs={failedJobs}
      onOpenMore={() => (moreOpen ? closeMore() : openMore())}
      moreOpen={moreOpen}
    />
  );

  /**
   * The sheet, and the bar under it.
   *
   * Wrapped so the sheet can be `absolute` against a box that also holds the bar — the bar stays
   * live and above it, because その他 is a toggle and pressing it again has to close.
   */
  const phoneFooter = isPhone ? (
    <div className="relative shrink-0">
      {moreOpen && (
        <MoreSheet
          active={active}
          failedJobs={failedJobs}
          onClose={closeMore}
          // **Forget, then navigate — never close, then navigate.** `closeOverlay` consumes the
          // sheet's entry with an async `history.back()`, so a push issued straight after it lands
          // first and the pop then undoes it: the tap did nothing at all. The push supersedes that
          // entry instead, which is what `forgetOverlay` is for.
          onNavigate={(id) => {
            setMoreOpenState(false);
            api.forgetOverlay("more");
            navigate(id);
          }}
        />
      )}
      {tabBar}
    </div>
  ) : undefined;

  /**
   * The confirm and prompt dialogs, wrapping every surface (日本-317).
   *
   * Here rather than beside `LightboxProvider` in `main.tsx` for two reasons. `navBridge.ts` says
   * there is one component above `App` and is meant to stay one, and this can take the api as a
   * prop — which is what that file asks for. And it is what makes the keyboard correct: capture
   * listeners fire earliest-first, React flushes a child's effects before its parent's, so a
   * provider *inside* `App` registers before the lightbox's and so beats it. See `Dialogs.tsx`.
   *
   * The five surface branches became `renderSurface` for this. It is a hoisted declaration, so it
   * reads in the order it always did.
   */
  // `Dialogs` is nested INSIDE `Menu`, and the order is load-bearing rather than arbitrary: both
  // register a capture-phase Escape at mount, capture fires earliest-first, and React flushes a
  // child's effects before its parent's — so the dialog registers first and owns Escape. That is
  // what makes a confirm opened from a menu behave: Escape answers the dialog, not the menu.
  return (
    <MenuProvider isPhone={isPhone}>
      <DialogsProvider nav={api}>{renderSurface()}</DialogsProvider>
    </MenuProvider>
  );

  function renderSurface() {
    if (active === "read") {
    return (
      <div className="flex h-full min-h-0 bg-void">
        {!isPhone && (
          <Rail
            active={active}
            onNavigate={navigate}
            dueCount={dueCount}
            failedJobs={failedJobs}
            synced={synced}
          />
        )}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {/* The tab bar goes INSIDE Read on a phone: the bottom sheet has to sit above it, and it
              is the sheet that owns the vertical space below the log. */}
          <Read
            log={readingLog}
            isPhone={isPhone}
            footer={phoneFooter}
            counts={counts}
            onAdjustCounts={adjustCounts}
            // 開く / `O` on a 文法 row (日本-519). A navigation, so it is `App`'s: pushing an
            // entry is a place, which is what lets Back close it.
            onOpenGrammar={(entryId) => api.navigate({ tab: "grammar", grammar: { entry: entryId } })}
            view={nav.read.view}
            moving={nav.read.moving}
            onOpenLibrary={(moving = null) =>
              api.navigate({ read: { view: "library", moving } })
            }
            onCloseLibrary={() => api.navigate({ read: { view: "log", moving: null } })}
            overlay={api}
            // The global Read shortcuts (日本-710). Only this branch passes it, which is the whole
            // of "nothing happens unless Read is open": on any other surface Read is not mounted.
            agentAction={agentAction}
          />
        </div>
      </div>
    );
  }

  if (active === "cards") {
    return (
      <div className="flex h-full min-h-0 bg-void">
        {!isPhone && (
          <Rail
            active={active}
            onNavigate={navigate}
            dueCount={dueCount}
            failedJobs={failedJobs}
            synced={synced}
          />
        )}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {/* The tab bar goes INSIDE Cards on a phone, for the reason Read's does: the note is a
              bottom sheet there, and a sheet at full height would bury its own grip if the bar were
              a sibling out here. */}
          <Cards
            isPhone={isPhone}
            footer={phoneFooter}
            filters={nav.cards}
            onFiltersChange={setCardFilters}
            navEpoch={epoch}
          />
        </div>
      </div>
    );
  }

  if (active === "review") {
    return (
      <div className="flex h-full min-h-0 bg-void">
        {!isPhone && (
          <Rail
            active={active}
            onNavigate={navigate}
            dueCount={dueCount}
            failedJobs={failedJobs}
            synced={synced}
          />
        )}
        {/* The tab bar nests INSIDE this column for the reason Stats records — the outer div is a
            row, for the rail, so a sibling out here becomes a second COLUMN. Review is the one
            surface that also *withholds* it: during a sitting there is no tab bar at all, because
            `✕` is the way out and a destination button under a grading button is a mis-tap that
            leaves the session. `Review` decides, which is why the bar is a prop rather than drawn
            here. */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <Review
            isPhone={isPhone}
            footer={phoneFooter}
            deck={nav.review.deck}
            onDeckChange={(deck, opts) => api.navigate({ review: { deck } }, opts)}
            onOpenStats={() => api.navigate({ tab: "stats" })}
            onOpenNote={(noteId) =>
              // Editing mid-review hands the note to Cards, which owns the editor. One patch, so
              // Back returns to the session rather than to Cards showing the same search.
              api.navigate({
                tab: "cards",
                cards: { ...EMPTY_CARDS_URL_STATE, deck: null, text: `nid:${noteId}` },
              })
            }
            onCollectionChanged={notifyCollectionChanged}
          />
        </div>
      </div>
    );
  }

  if (active === "stats") {
    return (
      <div className="flex h-full min-h-0 bg-void">
        {!isPhone && (
          <Rail
            active={active}
            onNavigate={navigate}
            dueCount={dueCount}
            failedJobs={failedJobs}
            synced={synced}
          />
        )}
        {/* The tab bar belongs INSIDE this column, not beside it. The outer div is a row — it has
            to be, for the rail — so a `{phoneFooter}` sibling OUT THERE lays the bar out as a
            second column and squeezes the surface into what is left. It renders, it does not
            overflow, and `check-stats.mjs`'s horizontal-scroll assertion passes throughout; only
            looking at it catches this. Every other surface already nests it this way. */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <Stats
            tab={nav.stats.tab}
            deck={nav.stats.deck}
            onTabChange={(tab) => api.navigate({ stats: { tab } })}
            onDeckChange={(deck) => api.navigate({ stats: { deck } })}
            // Calibration's empty state links to the job that fills it. One patch, so Back from
            // Setup returns to the tab that sent you rather than to Setup showing the same job.
            onOpenJob={() =>
              api.navigate({ tab: "setup", setup: { jobId: "stats-calibration" } })
            }
          />
          {phoneFooter}
        </div>
      </div>
    );
  }

  if (active === "search") {
    return (
      <div className="flex h-full min-h-0 bg-void">
        {!isPhone && (
          <Rail
            active={active}
            onNavigate={navigate}
            dueCount={dueCount}
            failedJobs={failedJobs}
            synced={synced}
          />
        )}
        {/* The footer goes INSIDE this column, never beside it — the outer div is a row, for the
            rail, so a sibling out here lays the bar out as a second column. Every surface nests it
            this way; Stats shipped for an hour without and only a screenshot caught it. */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <Search
            query={nav.search.q}
            // A refinement of where you already are, so `classify` replaces rather than pushes —
            // a history entry per keystroke is the thing `navHistory.ts` exists to refuse.
            onQueryChange={(q) => api.navigate({ search: { q } })}
            isPhone={isPhone}
            footer={phoneFooter}
            counts={counts}
            onAdjustCounts={adjustCounts}
            overlay={api}
          />
        </div>
      </div>
    );
  }

  if (active === "grammar") {
    return (
      <div className="flex h-full min-h-0 bg-void">
        {!isPhone && (
          <Rail
            active={active}
            onNavigate={navigate}
            dueCount={dueCount}
            failedJobs={failedJobs}
            synced={synced}
          />
        )}
        {/* The footer nests INSIDE this column, never beside it — the outer div is a row, for the
            rail, so a sibling out here lays the bar out as a second column. */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <Grammar
            query={nav.grammar.q}
            volume={nav.grammar.volume}
            partOfSpeech={nav.grammar.partOfSpeech}
            entryId={nav.grammar.entry}
            // The query and both filters REPLACE — refinements of where you already are, so a
            // history entry per keystroke is what `navHistory.ts` exists to refuse. Opening an
            // entry pushes, because it is a place: see `classify`.
            onQueryChange={(q) => api.navigate({ grammar: { q } })}
            onVolumeChange={(volume) => api.navigate({ grammar: { volume } })}
            onPartOfSpeechChange={(partOfSpeech) => api.navigate({ grammar: { partOfSpeech } })}
            onEntryChange={(entry) => api.navigate({ grammar: { entry } })}
            isPhone={isPhone}
            footer={phoneFooter}
          />
        </div>
      </div>
    );
  }

  if (active === "groups") {
    return (
      <div className="flex h-full min-h-0 bg-void">
        {!isPhone && (
          <Rail
            active={active}
            onNavigate={navigate}
            dueCount={dueCount}
            failedJobs={failedJobs}
            synced={synced}
          />
        )}
        {/* The footer nests INSIDE this column, never beside it — see Stats. A drill withholds it,
            as a 復習 sitting does: `✕` is the way out. */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <Groups
            groupId={nav.groups.group}
            drill={nav.groups.drill}
            filters={nav.groups.cards}
            onFiltersChange={setGroupFilters}
            // Opening a group resets its filters: they belong to the group you were in, and a
            // search typed inside one group would otherwise hide the next group's cards.
            onOpenGroup={(group, opts) =>
              api.navigate({ groups: { group, cards: EMPTY_CARDS_URL_STATE } }, opts)
            }
            onDrill={(drill, opts) => api.navigate({ groups: { drill } }, opts)}
            // Editing hands the note to 単語, which owns the editor — Review's precedent, and a
            // drill resumes where it was on Back.
            onOpenNote={(noteId) =>
              api.navigate({
                tab: "cards",
                cards: { ...EMPTY_CARDS_URL_STATE, deck: null, text: `nid:${noteId}` },
              })
            }
            navEpoch={epoch}
            isPhone={isPhone}
            footer={phoneFooter}
          />
        </div>
      </div>
    );
  }

  if (active === "setup") {
    return (
      <div className="flex h-full min-h-0 bg-void">
        {!isPhone && (
          <Rail
            active={active}
            onNavigate={navigate}
            dueCount={dueCount}
            failedJobs={failedJobs}
            synced={synced}
          />
        )}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <Setup
            isPhone={isPhone}
            openJobId={nav.setup.jobId}
            onOpenJob={(jobId) => api.navigate({ setup: { jobId } })}
            historyOpen={nav.setup.historyOpen}
            onHistoryOpenChange={(historyOpen) => api.navigate({ setup: { historyOpen } })}
            openDeckId={nav.setup.deckId}
            onOpenDeck={(deckId) => api.navigate({ setup: { deckId } })}
            onOpenCard={(noteId) => {
              // A job's error row opens the card it belongs to. Cards takes real Anki syntax in
              // its free-text field, so the deep link is just a search.
              //
              // `deck: null` is "All, explicitly" — a deck filter on top of an `nid:` lookup is how
              // the card you asked for vanishes. It used to need a special case inside `Cards`;
              // now it is the same encoding everything else uses.
              //
              // One patch, so it is one history entry: Back from the card returns to the job that
              // sent you, not to Cards showing the same search.
              api.navigate({
                tab: "cards",
                cards: { ...EMPTY_CARDS_URL_STATE, deck: null, text: `nid:${noteId}` },
              });
            }}
          />
          {phoneFooter}
        </div>
      </div>
    );
  }

  const current = destination(active);

  const body = current.ready ? (
    current.split ? (
      <SplitPane
        surface={current.id}
        defaultDockSize={current.id === "cards" ? 520 : 560}
        main={
          <div className="p-[26px] text-secondary text-ink-3">
            {current.label} — 一覧。未実装です。
          </div>
        }
        dock={
          <div className="p-[26px] text-secondary text-ink-3">
            {current.label} — 詳細ドック。未実装です。
          </div>
        }
      />
    ) : (
      <div className="flex-1 overflow-auto p-[22px] text-secondary text-ink-3">
        {current.label} — パネル。未実装です。
      </div>
    )
  ) : (
    <NotBuiltYet id={current.id} plannedIn="Stage 4, P2" />
  );

  return (
    <div className="flex h-full min-h-0 bg-void">
      {!isPhone && (
        <Rail
            active={active}
            onNavigate={navigate}
            dueCount={dueCount}
            failedJobs={failedJobs}
            synced={synced}
          />
      )}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <TopBar title={current.label} />
        {body}
        {phoneFooter}
      </div>
    </div>
    );
  }
}

/**
 * How many jobs failed on their last run and asked to be told.
 *
 * `notifyOnFailure` has no external channel — it means this badge and the banner inside Setup. So
 * the count has to be live from anywhere in the app: a scheduled job that failed at 03:00 and is
 * only discoverable by visiting Setup is not a notification.
 *
 * Refreshed on focus (App never unmounts, so a single read would be fixed for the session) and on
 * any job's status frame, which is what makes the badge clear itself the moment a retry passes.
 */
function useFailedJobCount(): number {
  const [count, setCount] = useState(0);

  const load = useCallback(() => {
    void listJobs()
      .then(({ jobs }) => {
        setCount(jobs.filter((j) => j.state === "failed" && j.trigger.notifyOnFailure).length);
      })
      // Needs the API; a badge of 0 beats a fabricated number when it cannot be read.
      .catch(() => undefined);
  }, []);

  useRefreshOnFocus(load);

  useEffect(() => subscribeToJobs({ onStatus: () => load() }), [load]);

  return count;
}
